import YAML from "@kyleshockey/js-yaml"  
import { Map } from "immutable"  
import parseUrl from "url-parse"  
import serializeError from "serialize-error"  
import isString from "lodash/isString"  
import debounce from "lodash/debounce"  
import set from "lodash/set"  
import { isJSONObject } from "core/utils"  
  
// Actions conform to FSA (flux-standard-actions)  
// {type: string,payload: Any|Error, meta: obj, error: bool}  
  
export const UPDATE_SPEC = "spec_update_spec"  
export const UPDATE_URL = "spec_update_url"  
export const UPDATE_JSON = "spec_update_json"  
export const UPDATE_PARAM = "spec_update_param"  
export const VALIDATE_PARAMS = "spec_validate_param"  
export const SET_RESPONSE = "spec_set_response"  
export const SET_REQUEST = "spec_set_request"  
export const SET_MUTATED_REQUEST = "spec_set_mutated_request"  
export const LOG_REQUEST = "spec_log_request"  
export const CLEAR_RESPONSE = "spec_clear_response"  
export const CLEAR_REQUEST = "spec_clear_request"  
export const CLEAR_VALIDATE_PARAMS = "spec_clear_validate_param"  
export const UPDATE_OPERATION_META_VALUE = "spec_update_operation_meta_value"  
export const UPDATE_RESOLVED = "spec_update_resolved"  
export const UPDATE_RESOLVED_SUBTREE = "spec_update_resolved_subtree"  
export const SET_SCHEME = "set_scheme"  
  
const toStr = (str) => isString(str) ? str : ""  
  
export function updateSpec(spec) {  
  const cleanSpec = (toStr(spec)).replace(/\t/g, "  ")  
  if(typeof spec === "string") {  
    return {  
      type: UPDATE_SPEC,  
      payload: cleanSpec  
    }  
  }  
}  
  
export function updateResolved(spec) {  
  return {  
    type: UPDATE_RESOLVED,  
    payload: spec  
  }  
}  
  
export function updateUrl(url) {  
  return {type: UPDATE_URL, payload: url}  
}  
  
export function updateJsonSpec(json) {  
  return {type: UPDATE_JSON, payload: json}  
}  
  
export const parseToJson = (str) => ({specActions, specSelectors, errActions}) => {  
  let { specStr } = specSelectors  
  
  let json = null  
  try {  
    str = str || specStr()  
    errActions.clear({ source: "parser" })  
    json = YAML.safeLoad(str)  
  } catch(e) {  
    // TODO: push error to state  
    console.error(e)  
    return errActions.newSpecErr({  
      source: "parser",  
      level: "error",  
      message: e.reason,  
      line: e.mark && e.mark.line ? e.mark.line + 1 : undefined  
    })  
  }  
  if(json && typeof json === "object") {  
    return specActions.updateJsonSpec(json)  
  }  
  return {}  
}  
  
let hasWarnedAboutResolveSpecDeprecation = false  
  
export const resolveSpec = (json, url) => ({specActions, specSelectors, errActions, fn: { fetch, resolve, AST = {} }, getConfigs}) => {  
  if(!hasWarnedAboutResolveSpecDeprecation) {  
    console.warn(`specActions.resolveSpec is deprecated since v3.10.0 and will be removed in v4.0.0; use requestResolvedSubtree instead!`)  
    hasWarnedAboutResolveSpecDeprecation = true  
  }  
  
  const {  
    modelPropertyMacro,  
    parameterMacro,  
    requestInterceptor,  
    responseInterceptor  
  } = getConfigs()  
  
  if(typeof(json) === "undefined") {  
    json = specSelectors.specJson()  
  }  
  if(typeof(url) === "undefined") {  
    url = specSelectors.url()  
  }  
  
  let getLineNumberForPath = AST.getLineNumberForPath ? AST.getLineNumberForPath : () => undefined  
  
  let specStr = specSelectors.specStr()  
  
  return resolve({  
    fetch,  
    spec: json,  
    baseDoc: url,  
    modelPropertyMacro,  
    parameterMacro,  
    requestInterceptor,  
    responseInterceptor  
  }).then( ({spec, errors}) => {  
      errActions.clear({  
        type: "thrown"  
      })  
      if(Array.isArray(errors) && errors.length > 0) {  
        let preparedErrors = errors  
          .map(err => {  
            console.error(err)  
            err.line = err.fullPath ? getLineNumberForPath(specStr, err.fullPath) : null  
            err.path = err.fullPath ? err.fullPath.join(".") : null  
            err.level = "error"  
            err.type = "thrown"  
            err.source = "resolver"  
            Object.defineProperty(err, "message", { enumerable: true, value: err.message })  
            return err  
          })  
        errActions.newThrownErrBatch(preparedErrors)  
      }  
  
      return specActions.updateResolved(spec)  
    })  
}  
  
let requestBatch = []  
  
const debResolveSubtrees = debounce(async () => {  
  const system = requestBatch.system // Just a reference to the "latest" system  
  
  if(!system) {  
    console.error("debResolveSubtrees: don't have a system to operate on, aborting.")  
    return  
  }  
    const {  
      errActions,  
      errSelectors,  
      fn: {  
        resolveSubtree,  
        AST = {}  
      },  
      specSelectors,  
      specActions,  
    } = system  
  
  if(!resolveSubtree) {  
    console.error("Error: Swagger-Client did not provide a `resolveSubtree` method, doing nothing.")  
    return  
  }  
  
  let getLineNumberForPath = AST.getLineNumberForPath ? AST.getLineNumberForPath : () => undefined  
  
  const specStr = specSelectors.specStr()  
  
  const {  
    modelPropertyMacro,  
    parameterMacro,  
    requestInterceptor,  
    responseInterceptor  
  } = system.getConfigs()  
  
  try {  
    var batchResult = await requestBatch.reduce(async (prev, path) => {  
      const { resultMap, specWithCurrentSubtrees } = await prev  
      const { errors, spec } = await resolveSubtree(specWithCurrentSubtrees, path, {  
        baseDoc: specSelectors.url(),  
        modelPropertyMacro,  
        parameterMacro,  
        requestInterceptor,  
        responseInterceptor  
      })  
  
      if(errSelectors.allErrors().size) {  
        errActions.clear({  
          type: "thrown"  
        })  
      }  
  
      if(Array.isArray(errors) && errors.length > 0) {  
        let preparedErrors = errors  
          .map(err => {  
            err.line = err.fullPath ? getLineNumberForPath(specStr, err.fullPath) : null  
            err.path = err.fullPath ? err.fullPath.join(".") : null  
            err.level = "error"  
            err.type = "thrown"  
            err.source = "resolver"  
            Object.defineProperty(err, "message", { enumerable: true, value: err.message })  
            return err  
          })  
        errActions.newThrownErrBatch(preparedErrors)  
      }  
  
      set(resultMap, path, spec)  
      set(specWithCurrentSubtrees, path, spec)  
  
      return {  
        resultMap,  
        specWithCurrentSubtrees  
      }  
    }, Promise.resolve({  
      resultMap: (specSelectors.specResolvedSubtree([]) || Map()).toJS(),  
      specWithCurrentSubtrees: specSelectors.specJson().toJS()  
    }))  
  
    delete requestBatch.system  
    requestBatch = [] // Clear stack  
  } catch(e) {  
    console.error(e)  
  }  
  
  specActions.updateResolvedSubtree([], batchResult.resultMap)  
}, 35)  
  
export const requestResolvedSubtree = path => system => {  
  requestBatch.push(path)  
  requestBatch.system = system  
  debResolveSubtrees()  
}  
  
export function changeParam( path, paramName, paramIn, value, isXml ){  
  return {  
    type: UPDATE_PARAM,  
    payload:{ path, value, paramName, paramIn, isXml }  
  }  
}  
  
export function changeParamByIdentity( pathMethod, param, value, isXml ){  
  return {  
    type: UPDATE_PARAM,  
    payload:{ path: pathMethod, param, value, isXml }  
  }  
}  
  
export const updateResolvedSubtree = (path, value) => {  
  return {  
    type: UPDATE_RESOLVED_SUBTREE,  
    payload: { path, value }  
  }  
}  
  
export const invalidateResolvedSubtreeCache = () => {  
  return {  
    type: UPDATE_RESOLVED_SUBTREE,  
    payload: {  
      path: [],  
      value: Map()  
    }  
  }  
}  
  
export const validateParams = ( payload, isOAS3 ) =>{  
  return {  
    type: VALIDATE_PARAMS,  
    payload:{  
      pathMethod: payload,  
      isOAS3  
    }  
  }  
}  
  
export function clearValidateParams( payload ){  
  return {  
    type: CLEAR_VALIDATE_PARAMS,  
    payload:{ pathMethod: payload }  
  }  
}  
  
export function changeConsumesValue(path, value) {  
  return {  
    type: UPDATE_OPERATION_META_VALUE,  
    payload:{ path, value, key: "consumes_value" }  
  }  
}  
  
export function changeProducesValue(path, value) {  
  return {  
    type: UPDATE_OPERATION_META_VALUE,  
    payload:{ path, value, key: "produces_value" }  
  }  
}  
  
export const setResponse = ( path, method, res ) => {  
  return {  
    payload: { path, method, res },  
    type: SET_RESPONSE  
  }  
}  
  
export const setRequest = ( path, method, req ) => {  
  return {  
    payload: { path, method, req },  
    type: SET_REQUEST  
  }  
}  
  
export const setMutatedRequest = ( path, method, req ) => {  
  return {  
    payload: { path, method, req },  
    type: SET_MUTATED_REQUEST  
  }  
}  
  
// This is for debugging, remove this comment if you depend on this action  
export const logRequest = (req) => {  
  return {  
    payload: req,  
    type: LOG_REQUEST  
  }  
}  
  
// Helper: check if a query param value is empty/default and should be excluded  
function isEmptyParamValue(value) {  
  return (  
    value === undefined ||  
    value === null ||  
    value === "" ||  
    value === "undefined" ||  
    value === "{}" ||  
    (typeof value === "object" && value !== null && Object.keys(value).length === 0)  
  )  
}  
  
// Helper: replace {paramName} placeholders in the path with actual values  
function buildUrl(baseUrl, pathName, parameters) {  
  let url = pathName  
  
  // Replace path parameters: {companyId}, {userId}, etc.  
  Object.keys(parameters).forEach(key => {  
    if (key.startsWith("path.")) {  
      const paramName = key.replace("path.", "")  
      url = url.replace(`{${paramName}}`, encodeURIComponent(parameters[key]))  
    }  
  })  
  
  // Build query string from query parameters, skipping empty values  
  const queryParts = []  
  Object.keys(parameters).forEach(key => {  
    if (key.startsWith("query.")) {  
      const paramName = key.replace("query.", "")  
      const value = parameters[key]  
      if (!isEmptyParamValue(value)) {  
        queryParts.push(`${encodeURIComponent(paramName)}=${encodeURIComponent(value)}`)  
      }  
    }  
  })  
  
  if (queryParts.length > 0) {  
    url = url + "?" + queryParts.join("&")  
  }  
  
  return baseUrl + url  
}  
  
// Helper: attach the authorized security schemes (oauth2 / apiKey / basic) to the  
// outgoing headers. swagger-client normally does this inside buildRequest/execute;  
// since we build the request manually here, we must attach the credentials  
// ourselves, otherwise the OAuth2 Bearer token is never sent.  
function applySecurities(headers, op, securities) {  
  securities = securities || {}  
  const authorized = securities.authorized || {}  
  const securityReqs = (op && op.security) || securities.specSecurity || []  
  
  securityReqs.forEach(requirement => {  
    Object.keys(requirement || {}).forEach(name => {  
      const auth = authorized[name]  
      if (!auth) {  
        return  
      }  
      const schema = auth.schema || {}  
      const type = schema.type  
      if (type === "oauth2") {  
        const token = auth.token || {}  
        let tokenType = token.token_type  
        if (!tokenType || tokenType.toLowerCase() === "bearer") {  
          tokenType = "Bearer"  
        }  
        if (token.access_token) {  
          headers["Authorization"] = tokenType + " " + token.access_token  
        }  
      } else if (type === "apiKey" && schema.in === "header" && schema.name) {  
        if (auth.value) {  
          headers[schema.name] = auth.value  
        }  
      } else if ((type === "http" && (schema.scheme || "").toLowerCase() === "basic") || type === "basic") {  
        if (auth.username != null) {  
          headers["Authorization"] = "Basic " + btoa(auth.username + ":" + (auth.password || ""))  
        }  
      }  
    })  
  })  
  
  return headers  
}  
  
// Actually fire the request via manual URL construction  
// Bypasses fn.buildRequest/fn.execute to avoid url-template createScanner issue  
export const executeRequest = (req) =>  
  ({fn, specActions, specSelectors, getConfigs, oas3Selectors}) => {  
    let { pathName, method, operation } = req  
    let { requestInterceptor } = getConfigs()  
  
    let op = operation.toJS()  
  
    // if url is relative, parseUrl makes it absolute by inferring from `window.location`  
    req.contextUrl = parseUrl(specSelectors.url()).toString()  
  
    if(op && op.operationId) {  
      req.operationId = op.operationId  
    } else if(op && pathName && method) {  
      req.operationId = fn.opId(op, pathName, method)  
    }  
  
    // Determine base URL  
    let baseUrl = ""  
    if(specSelectors.isOAS3()) {  
      const namespace = `${pathName}:${method}`  
  
      req.server = oas3Selectors.selectedServer(namespace) || oas3Selectors.selectedServer()  
  
      const namespaceVariables = oas3Selectors.serverVariables({  
        server: req.server,  
        namespace  
      }).toJS()  
      const globalVariables = oas3Selectors.serverVariables({ server: req.server }).toJS()  
  
      req.serverVariables = Object.keys(namespaceVariables).length ? namespaceVariables : globalVariables  
  
      req.requestContentType = oas3Selectors.requestContentType(pathName, method)  
      req.responseContentType = oas3Selectors.responseContentType(pathName, method) || "*/*"  
      const requestBody = oas3Selectors.requestBodyValue(pathName, method)  
  
      if(isJSONObject(requestBody)) {  
        req.requestBody = JSON.parse(requestBody)  
      } else if(requestBody && requestBody.toJS) {  
        req.requestBody = requestBody.toJS()  
      } else{  
        req.requestBody = requestBody  
      }  
  
      // Build base URL from server + server variables  
      let serverUrl = req.server || ""  
      if (req.serverVariables) {  
        Object.keys(req.serverVariables).forEach(variable => {  
          serverUrl = serverUrl.replace(`{${variable}}`, req.serverVariables[variable])  
        })  
      }  
      // If server URL is relative, make it absolute  
      if (serverUrl && !serverUrl.startsWith("http")) {  
        serverUrl = parseUrl(specSelectors.url()).toString().replace(/\/$/, "") + serverUrl  
      }  
    baseUrl = serverUrl  
    } else {  
      // OAS2: build base URL from spec host, basePath, scheme  
      let spec = req.spec || {}  
      let scheme = req.scheme || "https"  
      let host = spec.host || window.location.host  
      let basePath = spec.basePath || ""  
      baseUrl = `${scheme}://${host}${basePath}`  
    }  
  
    // Fallback: if baseUrl is still empty, use the spec URL origin or current window location  
    if (!baseUrl) {  
      let specUrl = specSelectors.url()  
      if (specUrl) {  
        let parsed = parseUrl(specUrl)  
        baseUrl = `${parsed.protocol}//${parsed.host}`  
      } else {  
        baseUrl = `${window.location.protocol}//${window.location.host}`  
      }  
    }
  
    // Build the full URL with path and query parameters  
    let parameters = req.parameters || {}  
    let fullUrl = buildUrl(baseUrl, pathName, parameters)  
  
    // Build headers  
    let headers = {}  
    Object.keys(parameters).forEach(key => {  
      if (key.startsWith("header.")) {  
        const headerName = key.replace("header.", "")  
        headers[headerName] = parameters[key]  
      }  
    })  
  
    if (req.requestContentType) {  
      headers["Content-Type"] = req.requestContentType  
    }  
    if (req.responseContentType) {  
      headers["Accept"] = req.responseContentType  
    }  
  
    // Attach OAuth2 Bearer / apiKey / basic credentials from the authorized state.  
    // Without this the manually-built request never carries the token (the bug).  
    applySecurities(headers, op, req.securities)  
  
    // Build the fetch request object  
    let fetchRequest = {  
      url: fullUrl,  
      method: method.toUpperCase(),  
      headers: headers  
    }  
  
    // Add request body for POST/PUT/PATCH  
    if (req.requestBody && ["post", "put", "patch"].indexOf(method.toLowerCase()) > -1) {  
      if (typeof req.requestBody === "object") {  
        fetchRequest.body = JSON.stringify(req.requestBody)  
      } else {  
        fetchRequest.body = req.requestBody  
      }  
    }  
  
    // Log the request for curl display  
    specActions.setRequest(req.pathName, req.method, fetchRequest)  
  
    // Apply request interceptor  
    if (requestInterceptor) {  
      let mutatedRequest = requestInterceptor(fetchRequest)  
      specActions.setMutatedRequest(req.pathName, req.method, Object.assign({}, mutatedRequest))  
      fetchRequest = mutatedRequest  
    }  
  
    // Track duration of request  
    const startTime = Date.now()  
  
    return fn.fetch(fetchRequest)  
      .then(res => {  
        res.duration = Date.now() - startTime  
        specActions.setResponse(req.pathName, req.method, res)  
      })  
      .catch(err => {  
        // swagger-client's fetch rejects on non-2xx with the full response attached  
        // as `err.response`. The SET_RESPONSE reducer merges `err.response` into the  
        // rendered result, so passing the serialized error surfaces the real HTTP  
        // status, headers and body instead of a bare "Undocumented" error.  
        if (err.response) {  
          err.response.duration = Date.now() - startTime  
        }  
        // A genuine network/CORS failure has no response - the browser hides the  
        // details, so replace the opaque "Failed to fetch" with actionable reasons.  
        if (err.message === "Failed to fetch") {  
          err.name = ""  
          err.message = "**Failed to fetch**.  \n**Possible Reasons:** \n  - CORS \n  - Network Failure \n  - URL scheme must be \"http\" or \"https\" for CORS request."  
        }  
        specActions.setResponse(req.pathName, req.method, {  
          error: true,  
          err: serializeError(err)  
        })  
      })  
  }  
  
// I'm using extras as a way to inject properties into the final, `execute` method - It's not great. Anyone have a better idea? @ponelat  
export const execute = ( { path, method, ...extras }={} ) => (system) => {  
  let { fn:{fetch}, specSelectors, specActions } = system  
  let spec = specSelectors.specJsonWithResolvedSubtrees().toJS()  
  let scheme = specSelectors.operationScheme(path, method)  
  let { requestContentType, responseContentType } = specSelectors.contentTypeValues([path, method]).toJS()  
  let isXml = /xml/i.test(requestContentType)  
  let parameters = specSelectors.parameterValues([path, method], isXml).toJS()  
  
  return specActions.executeRequest({  
    ...extras,  
    fetch,  
    spec,  
    pathName: path,  
    method, parameters,  
    requestContentType,  
    scheme,  
    responseContentType  
  })  
}  
  
export function clearResponse (path, method) {  
  return {  
    type: CLEAR_RESPONSE,  
    payload:{ path, method }  
  }  
}  
  
export function clearRequest (path, method) {  
  return {  
    type: CLEAR_REQUEST,  
    payload:{ path, method }  
  }  
}  
  
export function setScheme (scheme, path, method) {  
  return {  
    type: SET_SCHEME,  
    payload: { scheme, path, method }  
  }  
}



// WEBPACK FOOTER //
// ./src/core/plugins/spec/actions.js